"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Loader2,
  Check,
  X,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Layers,
  Clock,
  Square,
  Inbox,
} from "lucide-react";
import { useSwapTasks } from "@/stores/swap-tasks-store";
import { apiFetch } from "@/lib/api-fetch";
import { toast } from "sonner";
import { ShotPreviewEntry } from "@/components/editor/shot-preview-overlay";

/**
 * 后台任务顶层队列浮窗（2026-10-01 老板指令）。
 *
 * 「把所有后台推理的 llm、文生图、图生图、图生视频、文生视频 等任务都在前端用顶层
 *   队列浮窗的形式呈现出来，方便了解进度、已用时间等信息。」
 *
 * 老板随后追加：「把换脸托盘并进队列浮窗一起显示」——
 * 所以这里同时是**唯一**的后台队列入口：换脸任务（`swap-tasks-store`，客户端）与
 * 服务端活动（`/api/activity`：飞行中的 LLM/出图/出视频/配音 + `job_leases` 落库作业）
 * 合成一张列表，不再各自飘一个托盘。
 *
 * 三段时间语义不同，别混：
 *   · live      飞行中（内存）—— 排队中显创建时刻、处理中才显已用时长
 *   · lease     落库批作业 —— 有 done/total 真进度，跨刷新还在
 *   · swap      换脸（客户端 store）—— 关掉弹窗也继续跑
 *
 * ── 2026-10-17 第四次排版重做（老板：「优化这部分的功能布局，要求美观、简洁、交互方便」）──
 * 旧版的病根不是「不好看」，是**信息重复 + 横带太多**：
 *   ① 6 条横带（头部 / 动作条 / 标签页 / 段落标题 / 列表 / 页脚说明）压在 320px 宽里，
 *      真正的内容只剩 4 行可见；
 *   ② 同一组数字写三遍：头部「3 1」、标签页「处理中 3」、段落标题「处理中 · 3」；
 *   ③ 「回到顶部」是整块实心主色胶囊 —— 全局面板里最重的元素，却只是个辅助动作，
 *      与「当前选中标签页」抢视觉焦点；
 *   ④ 排队行右列 `创建于 10月17日 22:45` 在 92px 定宽里折成两行，把行高与右侧操作钮带偏；
 *   ⑤ 每行两个前置图标（状态 + 类型），类型图标 90% 的行都一样，纯占宽。
 *
 * 这一版按五条收敛（不改任何数据口径与操作能力）：
 *   ① **横带从 6 条降到 4 条**：头部（标题 + 摘要 + 窗口控制）、标签页、列表、页脚（动作）。
 *      动作条并入页脚，「清除」不再单独占一行，刷新说明整行删掉（信息进 title）。
 *   ② **数字只出现一次**：计数只在标签页上；段落标题退化为「圆点 + 名称 + 细线」的分隔符。
 *      空的段落直接不渲染（旧版会留一行「没有排队中的任务」，全是噪音）。
 *   ③ **标签页改分段控件**：浅灰轨道 + 选中项白底浮起，主色只用在数字上。
 *      比三个实心药丸安静，也更容易看出「当前在哪一栏」。
 *   ④ **行内重排**：状态图标独占前置槽（类型图标删除），标题与时间同一基线，
 *      时间 `whitespace-nowrap` 不再折行；进度条贴齐文本列，排队行不再画空轨道。
 *   ⑤ **窗口控制上移进头部**：回到顶部 / 收起 / 关闭三个图标钮，
 *      回到顶部保留主色底（上一轮「太不明显」的反馈不能回退），但缩成 24px 图标钮。
 *
 * ── 2026-10-03 重做排版/交互（老板：「美化该区域的排版及ui，ux交互」）─────────
 * 旧版毛病：控件碎（一堆文字按钮挤在标题行）、状态词与计时混着看、右列时间不齐、
 * 每行只有「图标+两行字」没有视觉锚点、主机地址整串 URL 铺满半行。
 * 这一版按四条改：
 *   ① **两段式头部**：上行=标题+计数（整行可折叠），下行=动作条（回到顶部/清除/关闭），
 *      动作与控制分离，不再跟标题争位置。
 *   ② **行内固定网格**：状态图标（排队=时钟 / 处理=转圈 / 成败=对勾、叉）/ 文本列 /
 *      右列定宽时间（`w-[92px] text-right`），跨行对齐。
 *   ③ **状态可视**：排队=琥珀胶囊+空轨道；处理=主色胶囊+脉冲进度条（真实百分比拿不到，
 *      不编数字）；批作业=done/total 真进度条。层级一眼扫得出来。
 *   ④ **主机信息收短**：`comfyui http://192.168.3.190:8000` → `comfyui · 192.168.3.190:8000`。
 *
 * ── 2026-10-03 标签页 + 分页（老板：「直接再加一个 已完成 的标签页按钮，处理中 和 排队中
 *    全部任务 也增加标签页按钮功能，点击后显示相应的内容（一次最多显示20条，大于20条翻页）」）
 * 三个标签页各自成一页内容，每页 20 条，超过 20 条才出现翻页条。要点：
 *   ① 标签页取代了原来的「正在运行 · N」/「刚结束 · N」两段小标题 —— 同一批数据按状态分栏，
 *      不再让「在跑」和「刚结束」挤在一个滚动流里靠标题分隔。
 *   ② 「已完成」有**两个来源，缺一不可**：
 *      · 本会话（内存活动表 + 换脸 store）—— 单张出图/出视频这类**不落库作业**的完成项；
 *      · 历史终态作业（`job_leases` 里 status != running，保留 7 天）—— 批作业历史，跨刷新都在。
 *      只取内存会在刷新后变空，只取历史会漏掉单张任务，所以合并后再统一排序分页。
 *   ③ 分页在前端做（服务端只按需回传「前 page*20 条」历史）：合并列表里既有内存项又有历史项，
 *      用服务端 offset 会在同一页里对不齐；多取一点由前端切页，逻辑只有一处。
 *   ④ 计数口径：标签页上的数字是**该栏总数**（不是当前页条数），翻页条再显「第 x/y 页 · 共 N 条」。
 */

type ActivityKind = "llm" | "t2i" | "i2i" | "t2v" | "i2v" | "tts" | "other";

/**
 * 🔴 2026-10-18：真实进度预估（服务端算，见 `lib/jobs/eta.ts`）。
 * 「处理中 / 完成」两个状态读不出还要等多久 —— 这里给出**预计总耗时、剩余时间、百分比**，
 * 依据是「同类型任务最近 N 次的单件耗时」×「这一单的尺寸/字符量」。
 */
interface EtaView {
  totalMs: number;
  remainingMs: number;
  pct: number;
  overrun: boolean;
  basis: "history" | "prior";
  samples: number;
}

interface LiveActivity {
  id: string;
  kind: ActivityKind;
  label: string;
  status: "running" | "done" | "error";
  startedAt: number;
  finishedAt?: number;
  detail?: string;
  queuePosition?: number | null;
  /** promptId 在不在渲染服 /queue 里（区分「排队等着」与「压根不在队里/被挤掉」） */
  inQueue?: boolean;
  /** 该活动的渲染服这次探到了没有；false = 探活失败/超时，位次拿不到 → 不能据此说它在跑 */
  queueProbeOk?: boolean;
  /** 远端渲染服地址；为空 = 本地任务（LLM/图生图），从未在远端排过队 */
  remoteBaseUrl?: string;
  /** 服务端记的「处理中」起点（见 /api/activity）：刷新页面也不丢 */
  processingStartedAt?: number | null;
  /** 远端渲染任务的 prompt_id（有它才可能被取消；本地调用取消不了） */
  promptId?: string;
  /** 预计耗时 / 剩余 / 百分比（仅处理中的行有） */
  eta?: EtaView;
}

interface LeaseRow {
  id: string;
  kind: string;
  /**
   * 作业域：空 = 整批；非空（如 shotId / sceneId）= 单项作业。
   *
   * 🔴 2026-10-15（老板反馈「视频提示词生成中的后台任务看不到」）：
   *   标签此前**只按 kind 映射**，单镜生成（kind=video_prompt, scope=<shotId>）被标成
   *   「视频提示词批」—— 用户读成「批量任务」，**以为自己那镜的任务没出现**（其实在，
   *   只是名字不对）。租约一直是分开建的（键 = kind:projectId:scope），纯粹是显示问题。
   */
  scope?: string | null;
  /** 🔴 2026-10-15：scope 为 shotId 时由服务端解析出的镜号（显示「第 N 镜」用） */
  shotSequence?: number | null;
  status: string;
  total?: number | null;
  done?: number | null;
  failed?: number | null;
  note?: string | null;
  startedAt?: string | number | Date | null;
  finishedAt?: string | number | Date | null;
  /** 预计耗时 / 剩余 / 百分比（仅处理中的行有） */
  eta?: EtaView;
}

/** 标签页键（处理中 / 排队中 / 已完成） */
type DockTab = "processing" | "queued" | "done";

/** 每页条数 —— 老板口径：一次最多显示 20 条，大于 20 条翻页 */
const PAGE_SIZE = 20;

const KIND_TEXT: Record<ActivityKind, string> = {
  llm: "文本推理",
  t2i: "文生图",
  i2i: "图生图",
  t2v: "文生视频",
  i2v: "图生视频",
  tts: "配音",
  other: "任务",
};

/**
 * 租约行标题 —— 必须区分「整批」与「单项」。
 *
 * 🔴 事故（2026-10-15）：只按 kind 打标签 ⇒ 单镜生成显示成「视频提示词批」，
 *   用户以为自己的任务没出现。单镜的租约键与批量**不同**（scope=shotId vs ""），
 *   一直是两条独立租约，只是显示上被抹平了。
 */
function leaseLabel(l: LeaseRow): string {
  const base = LEASE_TEXT[l.kind] ?? l.kind;
  if (!l.scope) return base;
  // 🔴 多镜并行时「单镜」无法区分是哪一镜 —— 服务端已把 shotId 解析成镜号，这里带上
  const shot = typeof l.shotSequence === "number" ? `（第 ${l.shotSequence} 镜）` : "";
  return `${base.replace(/批$/, "")} · 单镜${shot}`;
}

const LEASE_TEXT: Record<string, string> = {
  scene_image: "场景图批",
  prop_image: "道具图批",
  shot_ref_image: "镜头参考图",
  // 🔴 2026-10-03 推理任务统一入队：新租约类型的中文名
  video_prompt: "视频提示词批",
  shot_split: "分镜生成",
  video_gen: "视频生成",
  ref_video: "参考视频",
  frame_gen: "首尾帧",
  voice: "配音合成",
  assemble: "成片合成",
  character_image: "角色图批",
};

/* ── 行内排版常量：一处定义跨行复用（对齐靠它，不靠每行手写 class） ── */

/** 一行：状态槽（16px 定宽）+ 文本列 + 操作槽（20px 定宽，无操作时也占位，保证时间列右缘齐） */
const ROW = "group flex items-start gap-2 px-3 py-2 transition-colors hover:bg-[var(--surface)]";
const STATUS_SLOT = "mt-[3px] flex h-4 w-4 shrink-0 items-center justify-center";
const ACTION_SLOT = "flex h-5 w-5 shrink-0 items-center justify-center";
const PILL =
  "inline-flex h-[15px] shrink-0 items-center rounded-full px-1.5 text-[9px] font-semibold leading-none";
const TRACK = "mt-1.5 h-1 w-full overflow-hidden rounded-full bg-gray-100";
const HEAD_ICON_BTN =
  "flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

/**
 * 🔴 2026-10-18（老板：回到顶端要明显一点）：
 * 「回到顶部」在折叠胶囊与展开面板里各有一处，必须**同一套样式**——两处不同款
 * 是这类小控件最容易出现的漂移（一处主色实心、一处灰箭头，用户会以为是两个功能）。
 * 主色淡底 + 内描边，中性观感里唯一的主色小胶囊，够显眼但不抢主操作。
 */
const BACK_TO_TOP_BTN =
  "flex h-6 shrink-0 items-center justify-center gap-0.5 rounded-full bg-primary/10 px-1.5 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

function ms(v: string | number | Date | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v < 1e12 ? v * 1000 : v;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
}

function fmtElapsed(from: number, now: number): string {
  const s = Math.max(0, Math.floor((now - from) / 1000));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * 🔴 2026-10-18：把预估的毫秒数写成 `mm:ss`（超过 1 小时才带小时位）。
 * 与 `fmtElapsed` 分开是因为后者吃的是两个时间戳、且从不带小时 —— 视频批跑过 1 小时很常见，
 * 那种情况下 `95:12` 比 `01:35:12` 难读。
 */
function fmtDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m >= 60) {
    return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * 预估文案（处理中的行专用）。
 *   · 未超时 → `还需 02:39 · 47%`
 *   · 已超时 → `超出预计 01:20 · 99%`（不装作还在推进，条子同时换琥珀色）
 * 依据写进 title：`依据 8 个同类样本 · 预计总耗时 05:10`（没样本时说「经验值」）。
 */
function etaText(eta: EtaView, elapsedMs: number): { text: string; tip: string; overrun: boolean } {
  const overrun = eta.overrun;
  const text = overrun
    ? `超出预计 ${fmtDuration(elapsedMs - eta.totalMs)} · ${eta.pct}%`
    : `还需 ${fmtDuration(eta.remainingMs)} · ${eta.pct}%`;
  const basis = eta.basis === "history" ? `依据 ${eta.samples} 个同类样本` : "尚无同类样本，按经验值";
  const tip = `${basis} · 预计总耗时 ${fmtDuration(eta.totalMs)} · 已用 ${fmtDuration(elapsedMs)}`;
  return { text, tip, overrun };
}

/**
 * 🔴 2026-10-03（用户要求）：「排队中」显示**创建时间**（几月几日几点几分），不显示计时。
 * 排队时长不是「处理了多久」，拿它当进度看只会误判；要看就去看创建时刻。
 *
 * 🔴 2026-10-17：旧格式「10月17日 22:45」在 92px 定宽列里会折成两行（实测截图），
 * 把行高和右侧操作钮一起带偏。这里改短格式「10/17 22:45」—— 语义不变，宽度可控。
 */
function fmtClock(ts: number): string {
  const d = new Date(ts);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
}

/**
 * 🔴 2026-10-03：排队中 / 处理中 的判定（单一来源，别在 JSX 里各写一遍）。
 *
 * 口径（老板 2026-10-03 纠正）：「当前只有一个 comfyui 服务器在处理任务，每次只能单线程处理，
 * 正在处理的应该只有一条」。所以**走渲染服的任务**，只有渲染服 `/queue` 的 `queue_running`
 * 里明确有它（服务端折算成 `queuePosition === 0`）才算「处理中」，其余一律「排队中」——
 * 位次拿不到（探活失败、promptId 不在队里）时**没有证据说它在跑，就不给它挂秒表**。
 * （上一版靠「队列里有活但找不到我的 id ⇒ 也算排队」推断，`/queue` 一探活失败就全线失灵，
 *   齐刷刷显示「7 处理中」，实测被老板抓出来。）
 *
 * 为什么不写死「最多一条」：判据跟着渲染服的 `queue_running` 走 —— 将来加了多进程/多卡的
 * 生图生视频 API 或换多槽渲染服，同一时刻自然会出现多条「处理中」，这里一行都不用改。
 * 本地任务（LLM / 配音，`remoteBaseUrl` 为空）不过渲染服，本来就在处理。
 */
function isQueued(a: {
  status?: string;
  remoteBaseUrl?: string;
  queuePosition?: number | null;
}): boolean {
  // 🔴 已结束的不谈排队/处理中：服务端只给**在跑**的活动探队列，终态活动的 queuePosition 恒为 null，
  //    不拦这一句的话「已完成」栏里会冒出成排的「排队中 / 创建于 …」（实测就是这个 bug）。
  if (a.status && a.status !== "running") return false;
  if (!a.remoteBaseUrl) return false; // 本地任务：没走渲染服
  return a.queuePosition !== 0; // 0 = /queue 的 queue_running 里就是它
}

/** `comfyui http://192.168.3.190:8000` → `comfyui · 192.168.3.190:8000`（别让整串 URL 铺满半行） */
function shortHost(detail?: string): string | null {
  if (!detail) return null;
  const m = detail.match(/^(\S+)\s+(https?:\/\/\S+)$/);
  if (!m) return detail;
  try {
    return `${m[1]} · ${new URL(m[2]).host}`;
  } catch {
    return detail;
  }
}

/**
 * 段落分隔符（吸顶）。
 *
 * 🔴 2026-10-17：**不再带计数** —— 标签页上已经有同一组数字，40px 内写两遍只会互相打架。
 * 现在它只负责「这里换段了」这一件事：圆点 + 名称 + 一条细线。
 */
function SectionLabel({ tone, children }: { tone: string; children: ReactNode }) {
  return (
    <div className="sticky top-0 z-10 flex items-center gap-2 bg-white/95 px-3 pb-1 pt-2.5 backdrop-blur">
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${tone}`} />
      <span className="shrink-0 text-[10px] font-medium text-gray-400">{children}</span>
      <span className="h-px flex-1 bg-[var(--border-subtle)]" />
    </div>
  );
}

/** 整块为空时的空态：图标 + 一句话（比一行 11px 灰字更容易被看见） */
function EmptyState({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-10">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--surface)]">
        <Inbox className="h-4 w-4 text-gray-300" />
      </span>
      <p className="text-[11px] text-gray-400">{text}</p>
    </div>
  );
}

/** 某一段超过一页时的提示：这里只放一页，看全部去对应标签页 */
function MoreLine({ text }: { text: string }) {
  return (
    <div className="border-t border-[var(--border-subtle)] bg-[var(--surface)] px-3 py-1 text-[10px] text-gray-400">
      {text}
    </div>
  );
}

export function TaskQueueDock() {
  const [live, setLive] = useState<LiveActivity[]>([]);
  const [leases, setLeases] = useState<LeaseRow[]>([]);
  const [now, setNow] = useState(() => Date.now());
  /**
   * 🔴 2026-10-04（用户要求）：**默认收起**。
   * 旧默认 `true`（展开）—— 每次进页面右下角都铺开一大块面板，遮卡片。
   * 收起态是一条细胶囊，仍然实时显示「N 处理中 / N 排队」，点开才铺开完整面板。
   */
  const [expanded, setExpanded] = useState(false);
  const [hidden, setHidden] = useState(false);

  /* ── 标签页 + 页码（2026-10-03 用户要求）── */
  const [tab, setTab] = useState<DockTab>("processing");
  const [page, setPage] = useState(1);
  /** 「已完成」的历史部分（job_leases 终态；服务端按需回传，能翻页） */
  const [finishedRows, setFinishedRows] = useState<LeaseRow[]>([]);
  const [finishedTotal, setFinishedTotal] = useState(0);

  /**
   * 🔴 2026-10-03：远端任务「处理中」的起始时刻（前端兜底；服务端一般已给 processingStartedAt）。
   *
   * 秒表口径：**排队中不显示时长**（改显创建时间），进入「处理中」才开始计。
   * 远端任务从 startedAt 到真正开渲染之间可能夹着几十分钟排队 —— 拿 startedAt 当起点
   * 会把排队时间算进「已处理」，这正是旧界面上「进行中 63:49」的由来。
   * 这里记下**第一次观测到它不再排队**的时刻当起点（浮窗 2.5s 轮询，误差 ≤2.5s）；
   * 本地任务（无远端队列，如 LLM/图生图）从来没排过队，直接用 startedAt，精确。
   */
  const processSinceRef = useRef<Map<string, number>>(new Map());
  const processingSince = (a: LiveActivity): number => {
    if (a.processingStartedAt) return a.processingStartedAt; // 服务端记的更稳（跨刷新不丢）
    if (!a.remoteBaseUrl) return a.startedAt; // 本地任务：没排过队
    const hit = processSinceRef.current.get(a.id);
    if (hit !== undefined) return hit;
    if (a.queuePosition === 0) {
      const t = Date.now();
      processSinceRef.current.set(a.id, t);
      return t;
    }
    return a.startedAt; // 兜底：浮窗刚打开时它已在渲染，本进程没观测到「排队→处理」的切换
  };

  const swapTasks = useSwapTasks((s) => s.tasks);
  const dismissSwap = useSwapTasks((s) => s.dismiss);
  const clearSwapFinished = useSwapTasks((s) => s.clearFinished);
  /** 换脸任务类型（从 store 的返回类型里取，别另写一份形状） */
  type SwapTask = (typeof swapTasks)[number];

  /* ── 逐条管理：取消 / 删除（2026-10-03 用户：「所有处理的操作都要在后台任务里面
        实时呈现及管理（取消/删除）」）───────────────────────────────────────── */

  /** 本会话手动移除的内存记录（活动/换脸终态行）：服务端内存表 2 分钟自动过期，
      这里只是让用户别等这 2 分钟；不持久化 —— 刷新后历史本来就不在内存里了 */
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const dismissRow = (key: string) =>
    setDismissedIds((prev) => new Set(prev).add(key));

  /** 取消一条在跑任务（租约批作业 / 远端渲染活动）。返回是否已受理。
   *  silent=true 时不弹每条 toast（「全部停止」批量调用用，否则 N 条任务弹 N 个提示）。 */
  const cancelTask = async (target: "lease" | "activity", id: string, silent = false) => {
    try {
      const res = await apiFetch(`/api/activity/manage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target, id }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        reason?: string;
        message?: string;
        error?: string;
      };
      if (silent) return res.ok && data.ok;
      if (res.ok && data.ok) {
        toast.success(target === "lease" ? "已请求取消：当前项跑完后停止" : "已送达取消指令");
      } else if (data.reason === "local") {
        // 本地 LLM/配音：一个已在飞的 fetch，没有安全打断点 —— 如实说，别装取消成功
        toast.info(data.message ?? "本地推理请求不可中断，稍后自动结束");
      } else {
        toast.error(data.error ?? "取消失败");
      }
      return res.ok && data.ok;
    } catch {
      if (!silent) toast.error("取消请求发送失败");
      return false;
    }
  };

  /* ── 全部停止（2026-10-04 用户要求）：面板动作条一键停止所有批量作业 + 渲染任务。
      批量作业（lease）取消是协作式的：当前项跑完后停止，未跑的不再继续；
      远端渲染活动（activity）取消是硬中断。本地推理不可中断，不计入。
      🔴 2026-10-04（用户：「全部停止 要连同看门狗一起停止」）：同时停掉看门狗
      （孤儿回收/兜底清扫暂停），否则渲染服不响应时回收循环不断把作业行归位，
      「已停止」的系统看起来又在动。停机闩保证自愈入口不会把它悄悄拉回来；
      入队新任务时自动恢复。 ── */
  const [stoppingAll, setStoppingAll] = useState(false);
  const stopAllTasks = async () => {
    const leaseIds = runningLeases.map((l) => l.id);
    const actIds = runningLive
      .filter((a) => a.remoteBaseUrl && a.promptId)
      .map((a) => a.id);
    const hasRunning = leaseIds.length > 0 || actIds.length > 0;
    const summary = [
      leaseIds.length > 0 ? `${leaseIds.length} 个批量作业` : null,
      actIds.length > 0 ? `${actIds.length} 个渲染任务` : null,
    ]
      .filter(Boolean)
      .join(" + ");
    const confirmMsg = hasRunning
      ? `确定停止全部任务？（${summary} + 看门狗）\n批量作业：当前项跑完后停止，未跑的不再继续；渲染任务：立即中断；看门狗：僵尸回收暂停（重启门户前不再自动拉起）。`
      : "当前没有在跑的批量/渲染任务。要连同看门狗一起停止吗？\n（僵尸回收暂停；重启门户前不再自动拉起）";
    if (!window.confirm(confirmMsg)) {
      return;
    }
    setStoppingAll(true);
    try {
      const stopWatchdogReq = apiFetch(`/api/runtime/watchdog`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "stop" }),
      })
        .then((r) => r.ok)
        .catch(() => false);
      const results = await Promise.all([
        ...leaseIds.map((id) => cancelTask("lease", id, true)),
        ...actIds.map((id) => cancelTask("activity", id, true)),
        stopWatchdogReq,
      ]);
      const okCount = results.filter(Boolean).length;
      if (okCount === results.length) {
        toast.success(
          hasRunning
            ? `已停止全部任务（${summary} + 看门狗）`
            : "已停止看门狗（后台回收暂停，重启门户前不再自动拉起）",
        );
      } else {
        toast.warning(`停止指令已发送：${okCount}/${results.length} 条受理成功`);
      }
    } finally {
      setStoppingAll(false);
    }
  };

  /** 删除一条终态租约的历史记录（已完成/失败/已取消；在跑的会被后端 409 拒掉） */
  const deleteLeaseRow = async (id: string) => {
    try {
      const res = await apiFetch(`/api/activity/manage?leaseId=${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      if (res.ok) {
        setFinishedRows((prev) => prev.filter((l) => l.id !== id));
      } else {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        toast.error(data.error ?? "删除失败");
      }
    } catch {
      toast.error("删除请求发送失败");
    }
  };

  /** 行尾操作钮（谁可取消、谁可删除的判据都收在这一个函数里，别在三个 render 里各写一遍） */
  const rowAction = (
    kind: "cancel-lease" | "cancel-activity" | "dismiss-act" | "dismiss-swap" | "delete-lease",
    id: string,
    tip: string,
  ) => (
    <button
      type="button"
      aria-label={tip}
      onClick={(e) => {
        e.stopPropagation();
        if (kind === "cancel-lease") void cancelTask("lease", id);
        else if (kind === "cancel-activity") void cancelTask("activity", id);
        else if (kind === "dismiss-act") dismissRow(`act:${id}`);
        else if (kind === "dismiss-swap") dismissRow(`swap:${id}`);
        else void deleteLeaseRow(id);
      }}
      className="flex h-5 w-5 items-center justify-center rounded-md text-gray-300 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      title={tip}
    >
      {kind.startsWith("cancel") ? <X className="h-3 w-3" /> : <Trash2 className="h-3 w-3" />}
    </button>
  );

  /**
   * 轮询服务端活动（2.5s：够实时，又不至于把 dev server 打满）。
   *
   * 🔴 2026-10-03：`finishedLimit` 只在「已完成」栏才带 —— 没打开那一栏时不必为了一份
   * 用不上的历史去查库；翻到第 N 页就取前 N*20 条（前端合并排序后切页，见文件头 ③）。
   */
  useEffect(() => {
    let alive = true;
    // 合并视图（处理中栏）的第三段就是「已完成」，所以这里一直取
    const wantFinished = Math.min(page * PAGE_SIZE, 200);
    const pull = async () => {
      try {
        // 必须走 apiFetch：应用有 basePath（/plugins/dsh-image-gen/aicomic），
        // 裸 fetch("/api/activity") 会 404（实测日志里一串 404 就是它）
        const res = await apiFetch(`/api/activity?finishedLimit=${wantFinished}`);
        if (!res.ok) return;
        const data = (await res.json()) as {
          live?: LiveActivity[];
          leases?: LeaseRow[];
          finished?: { items?: LeaseRow[]; total?: number };
        };
        if (!alive) return;
        setLive(data.live ?? []);
        setLeases(data.leases ?? []);
        if (data.finished) {
          setFinishedRows(data.finished.items ?? []);
          setFinishedTotal(data.finished.total ?? 0);
        }
        setNow(Date.now());
      } catch {
        /* 网络抖动忽略 */
      }
    };
    pull();
    const t = setInterval(pull, 2500);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [tab, page]);

  // 秒表：每秒重算已用时间
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  /**
   * 🔴 2026-10-03（用户反馈「悬浮菜单的位置挡住底部菜单栏了」）：
   * 窄屏（<1024px）时 `project-sidebar` 会渲染一条 `fixed bottom-0` 的底部 tab 导航
   * （「剧本/角色/场景/我的」），浮窗是 `fixed bottom-4 right-4` 且 z-50 > 导航的 z-40
   * → 直接把导航压住（实测被挡的是「场景」「我的」）。
   * 这里**不写死偏移量**：量一次那条导航的真实高度，把浮窗抬到它上面。
   * 量不到（宽屏本来就没有这条导航 / 结构改了）就回到 16px，行为与以前一致。
   */
  const [bottomOffset, setBottomOffset] = useState(16);
  useEffect(() => {
    const measure = () => {
      const bar =
        document.querySelector<HTMLElement>("[data-bottom-nav]") ??
        document.querySelector<HTMLElement>("nav[class*='bottom-0'][class*='lg:hidden']");
      if (!bar) {
        setBottomOffset(16);
        return;
      }
      const h = Math.round(bar.getBoundingClientRect().height);
      setBottomOffset(h > 0 ? h + 12 : 16);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  /* ── 派生列表 ────────────────────────────────────────────────── */
  const runningLive = useMemo(() => live.filter((a) => a.status === "running"), [live]);
  const queuedLive = useMemo(() => runningLive.filter((a) => isQueued(a)), [runningLive]);
  const processingLive = useMemo(() => runningLive.filter((a) => !isQueued(a)), [runningLive]);
  // 🔴 终态行可被用户手动移除（2026-10-03「删除」）：dismissedIds 只拦终态行
  const recentLive = useMemo(
    () => live.filter((a) => a.status !== "running" && !dismissedIds.has(`act:${a.id}`)),
    [live, dismissedIds]
  );
  const runningSwap = useMemo(() => swapTasks.filter((t) => t.status === "running"), [swapTasks]);
  const finishedSwap = useMemo(
    () => swapTasks.filter((t) => t.status !== "running" && !dismissedIds.has(`swap:${t.id}`)),
    [swapTasks, dismissedIds]
  );
  const runningLeases = useMemo(() => leases.filter((l) => l.status === "running"), [leases]);
  const failedLeases = useMemo(
    () => leases.filter((l) => l.status === "failed" || l.status === "orphaned"),
    [leases]
  );

  const processingCount = processingLive.length + runningSwap.length + runningLeases.length;
  /** 🔴 2026-10-03（用户要求）「排队中 / 处理中」分开计数：排队的时长没意义，只有处理中的才在计时 */
  const queuedCount = queuedLive.length;

  /**
   * 「已完成」= 本会话刚结束的（内存活动表 + 换脸）+ 历史终态作业（job_leases）。
   * 合并后统一按结束时刻倒序 —— 同一个页面里两种来源混排，用户不会知道哪条来自哪边，
   * 顺序就按时间走，最直观。
   */
  type DoneEntry =
    | { key: string; at: number; src: "act"; a: LiveActivity }
    | { key: string; at: number; src: "swap"; t: SwapTask }
    | { key: string; at: number; src: "lease"; l: LeaseRow };

  const doneList = useMemo<DoneEntry[]>(() => {
    const out: DoneEntry[] = [];
    for (const a of recentLive) {
      out.push({ key: `act:${a.id}`, at: a.finishedAt ?? a.startedAt, src: "act", a });
    }
    for (const t of finishedSwap) {
      out.push({ key: `swap:${t.id}`, at: t.finishedAt ?? t.startedAt, src: "swap", t });
    }
    for (const l of finishedRows) {
      out.push({ key: `lease:${l.id}`, at: ms(l.finishedAt) ?? ms(l.startedAt) ?? 0, src: "lease", l });
    }
    return out.sort((x, y) => y.at - x.at);
  }, [recentLive, finishedSwap, finishedRows]);

  /** 「已完成」总数 = 本会话条数 + 历史总数（历史只回传了前若干条，总数得听服务端的） */
  const doneCount = recentLive.length + finishedSwap.length + finishedTotal;

  const failedCount =
    recentLive.filter((a) => a.status === "error").length +
    finishedSwap.filter((t) => t.status === "error").length +
    failedLeases.length;

  /** 折叠态胶囊上的「已完成」只讲本会话（没展开时服务端也没取历史，别编数字） */
  const finishedCount = recentLive.length + finishedSwap.length;

  /*
   * 这里**故意不做**「处理中为空就自动跳到排队中」：合并视图（处理中栏）本身就是三段连着排
   * ——处理中为空时它照样显示「排队中」和「已完成」两段，跳到一个单栏列表反而看到的东西更少。
   * 默认永远停在合并视图，用户手动切了才跟着走。
   */

  const nothing = live.length === 0 && swapTasks.length === 0 && leases.length === 0;
  if (nothing || hidden) return null;

  /**
   * 🔴 2026-10-03「回到顶部」（用户要求：悬浮框追加回到顶部按钮）。
   *
   * 分镜页能滚很长（几十张卡一路到底），浮窗却固定在右下角 —— 想回顶部只能自己一路拖回去。
   * 这里**不写死 window**：分镜页的滚动者是页面里的内层容器（实测 window 滚不动，
   * 桌面预览面板的翻页滚轮对这个页面也无效），浮窗又自己 fixed、不一定是那个容器的子孙。
   * 所以不猜：全文档扫一遍「谁 scrollTop > 0」就让它回顶，window 兜底。
   *
   * 🔴 2026-10-03（用户：「回到顶部这个按钮不能折叠」）：这个按钮是**页面级导航**，
   * 不是任务列表的一部分 —— 面板折叠成胶囊时它必须仍然可用。所以折叠态也渲染
   * （见下方折叠分支），本函数也因此定义在折叠分支之前。
   */
  const handleBackToTop = () => {
    // ① 全量扫可滚动容器：可能是 window，也可能是页面里任意一层 overflow-*-auto 的 div，
    //    甚至不是浮窗的祖先（浮窗自己 fixed）。不去猜 —— 谁滚下去了就让谁回顶。
    //    一次点击 O(n) 扫描（这页几万节点、几毫秒），比「猜错容器点了没反应」划算。
    const scrolled = Array.from(document.querySelectorAll<HTMLElement>("*")).filter(
      (el) => el.scrollTop > 0
    );
    for (const el of scrolled) el.scrollTo({ top: 0, behavior: "smooth" });
    // ② window 兜底（多数页面就是它；上面没命中时这条生效）
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* 🔴 2026-10-03（用户：「折叠的悬浮栏 还可以进一步优化、减小区域」）：
     旧折叠态还是**两行**（标题行 + 动作条），占屏 ~7%。折叠就该只剩一条：
     一行胶囊、只用数字说话，点开才铺开面板。清除 / 关闭都收进展开态的动作条 ——
     折叠时只回答一个问题「现在有多少活在跑」。
     🔴 唯独「回到顶部」例外（用户：「回到顶部这个按钮不能折叠」）：它是页面级导航，
     跟任务列表无关，折叠后仍单独成钮保留在旁边。 */
  if (!expanded) {
    /**
     * 🔴 2026-10-17（老板：「收起后的也要优化」）：折叠态从「三块各自飘着的白药丸」
     * 改成**一个胶囊里的三段**（顺序预览 │ 回顶 │ 后台任务 + 状态徽标）。
     *
     * 旧版的问题不是单个钮不好看，是**它们互不相干**：三块都自带 border + shadow-lg，
     * 中间隔 8px，看起来像三个遗留的小挂件；而且「后台任务」里塞了图标 + 4 个字 +
     * 转圈 + 3 个字 + 箭头，10px 字挤成一条，扫不出重点。
     *
     * 这一版：
     *   · 外层容器只留**一圈边 + 一层阴影**，内部用 1px 竖线分段 —— 视觉上是一个整体；
     *   · 状态收敛成一个**徽标**（3 处理中 / 1 排队 / 3 失败 / 566 已完成），
     *     文字与图标不再各占一段；
     *   · 主色只用在两处：播放图标、处理中的脉冲点 + 徽标底色 —— 其余一律中性灰。
     */
    const chip =
      processingCount > 0
        ? {
            tone: "bg-primary/10 text-primary",
            icon: <Loader2 className="h-2.5 w-2.5 animate-spin" />,
            text: `${processingCount} 处理中`,
          }
        : queuedCount > 0
          ? {
              tone: "bg-amber-50 text-amber-700",
              icon: <Clock className="h-2.5 w-2.5" />,
              text: `${queuedCount} 排队`,
            }
          : failedCount > 0
            ? {
                tone: "bg-red-50 text-red-600",
                icon: <X className="h-2.5 w-2.5" />,
                text: `${failedCount} 失败`,
              }
            : finishedCount > 0
              ? {
                  tone: "bg-emerald-50 text-emerald-700",
                  icon: <Check className="h-2.5 w-2.5" />,
                  text: `${finishedCount} 已完成`,
                }
              : null;

    return (
      <div
        style={{ bottom: bottomOffset }}
        className="fixed right-4 z-50 flex items-center gap-2"
      >
        <div className="flex max-w-[calc(100vw-2rem)] items-center gap-0.5 overflow-hidden rounded-full border border-[var(--border-subtle)] bg-white/95 p-1 shadow-lg shadow-black/10 backdrop-blur">
          {/**
            * 🔴 2026-10-18（老板：回到顶端要明显一点，且放到最左边）
            *
            * 旧形态是夹在「顺序预览」和「后台任务」中间的一个 24px **纯灰箭头**：
            * 既是最不显眼的一个，又占了最不容易找到的位置 —— 长分镜页里它是高频操作。
            * 现在：
            *   · **排到最左**（整条胶囊的第一个可点元素）；
            *   · 换成**主色淡底 + 内描边**的胶囊，并带「顶部」文字（手机上只留图标省宽）；
            *   · 与「顺序预览 / 后台任务」同属"带字的胶囊"，不再是一个异类小图标。
            * 语义上它仍是页面级导航（不属于任务面板），所以独立成段、用竖线隔开。
            */}
          <button
            type="button"
            aria-label="回到顶端"
            data-dock-action="back-to-top"
            onClick={handleBackToTop}
            title="回到页面最上方"
            className={`${BACK_TO_TOP_BTN} pl-1`}
          >
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
            <span className="hidden whitespace-nowrap pr-1 text-[10px] font-semibold sm:inline">顶部</span>
          </button>

          <span aria-hidden className="mx-0.5 h-3.5 w-px shrink-0 bg-[var(--border-subtle)]" />

          {/* 顺序预览入口：折叠/展开两态都渲染（2026-10-14 用户指定位置） */}
          <ShotPreviewEntry />

          <span aria-hidden className="mx-0.5 h-3.5 w-px shrink-0 bg-[var(--border-subtle)]" />

          <button
            type="button"
            onClick={() => setExpanded(true)}
            aria-expanded={false}
            data-dock-action="expand"
            title={`展开后台任务（${processingCount} 处理中 · ${queuedCount} 排队 · ${failedCount} 失败）`}
            className="group flex h-6 shrink-0 items-center gap-1.5 rounded-full pl-1.5 pr-1.5 transition-colors hover:bg-[var(--surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
              <Layers className="h-3.5 w-3.5 text-primary" />
            </span>
            <span className="whitespace-nowrap text-[10px] font-semibold text-gray-600">后台任务</span>
            {chip ? (
              <span
                className={`flex h-4 shrink-0 items-center gap-0.5 whitespace-nowrap rounded-full px-1.5 text-[9px] font-semibold ${chip.tone}`}
              >
                {chip.icon}
                {chip.text}
              </span>
            ) : null}
            <ChevronUp className="h-3 w-3 shrink-0 text-gray-400 transition-colors group-hover:text-gray-600" />
          </button>
        </div>
      </div>
    );
  }

  /* ── 三栏的内容（统一成 {key, node}，好让分页只写一处） ─────────────── */

  /**
   * 一行活动：**三种状态都要能画**（在跑-排队中 / 在跑-处理中 / 已结束）。
   *
   * 🔴 合并「已完成」栏时踩过：旧代码里「刚结束」是单写一段的，改成标签页后终态活动也走这个函数，
   *    于是必须自己认状态 —— 否则终态行会被当成「排队中」（`isQueued` 见下方守卫）。
   *
   * 🔴 2026-10-17 行内重排：状态图标独占前置槽（类型图标删除），标题与时间同一基线，
   *    时间 `whitespace-nowrap`；排队行不再画空轨道（那是纯噪音）。
   */
  const renderLiveRow = (a: LiveActivity) => {
    const ended = a.status !== "running";
    const ok = a.status === "done";
    const q = !ended && isQueued(a);
    const host = shortHost(a.detail);
    // 🔴 2026-10-18：处理中的行给出「还需 mm:ss · pct%」（服务端按同类历史 + 尺寸算）
    const elapsedMs = now - processingSince(a);
    const eta = !ended && !q ? a.eta : undefined;
    const etaInfo = eta ? etaText(eta, elapsedMs) : null;
    return (
      <div key={a.id} className={ROW}>
        <span className={STATUS_SLOT}>
          {ended ? (
            ok ? (
              <Check className="h-3.5 w-3.5 text-emerald-500" />
            ) : (
              <X className="h-3.5 w-3.5 text-red-500" />
            )
          ) : q ? (
            <Clock className="h-3.5 w-3.5 text-amber-500" />
          ) : (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-[12px] font-medium leading-5 text-gray-800" title={a.label}>
              {a.label}
            </span>
            <span
              className={`ml-auto shrink-0 whitespace-nowrap font-mono text-[10px] tabular-nums ${
                ended ? "text-gray-400" : q ? "text-gray-400" : "text-primary"
              }`}
            >
              {ended
                ? a.finishedAt
                  ? `耗时 ${fmtElapsed(a.startedAt, a.finishedAt)}`
                  : ""
                : q
                  ? fmtClock(a.startedAt)
                  : fmtElapsed(processingSince(a), now)}
            </span>
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-gray-400">
            {ended ? (
              ok ? (
                <span className={`${PILL} bg-emerald-50 text-emerald-700`}>完成</span>
              ) : (
                <span className={`${PILL} bg-red-50 text-red-600`}>失败</span>
              )
            ) : q ? (
              <span className={`${PILL} bg-amber-50 text-amber-700`}>排队中</span>
            ) : (
              <span className={`${PILL} bg-primary/10 text-primary`}>处理中</span>
            )}
            <span className="shrink-0">{KIND_TEXT[a.kind] ?? "任务"}</span>
            {etaInfo ? (
              <span
                className={`shrink-0 tabular-nums ${etaInfo.overrun ? "font-medium text-amber-600" : "text-primary/80"}`}
                title={etaInfo.tip}
              >
                {etaInfo.text}
              </span>
            ) : null}
            {ended && !ok && a.detail ? (
              <span className="truncate text-red-500" title={a.detail}>
                {a.detail}
              </span>
            ) : null}
            {!ended && q && typeof a.queuePosition === "number" && a.queuePosition > 0 ? (
              <span className="shrink-0 text-amber-600">第 {a.queuePosition} 位</span>
            ) : null}
            {!ended && q && a.queuePosition == null ? (
              <span className="shrink-0 text-amber-600/80">
                {a.queueProbeOk === false ? "渲染服未响应" : "未在渲染队列中"}
              </span>
            ) : null}
            {!ended && !q && host ? (
              <span className="truncate" title={a.detail}>
                {host}
              </span>
            ) : null}
          </div>
          {/* 进度：有预估就画**真实时间进度**（宽度 = pct，超时换琥珀色）；
              没有预估才退回脉冲 —— 脉冲只表示「在动」，读不出还剩多久。 */}
          {!ended && !q ? (
            <div className={TRACK} title={etaInfo?.tip}>
              {eta ? (
                <div
                  className={`h-full rounded-full transition-[width] duration-700 ${
                    etaInfo?.overrun ? "bg-amber-400" : "bg-primary"
                  }`}
                  style={{ width: `${Math.max(2, eta.pct)}%` }}
                />
              ) : (
                <div className="h-full w-1/3 animate-pulse rounded-full bg-primary/70" />
              )}
            </div>
          ) : null}
        </div>

        {/* 操作槽：无操作时也占位，保证所有行的时间右缘对齐 */}
        <span className={ACTION_SLOT}>
          {ended
            ? rowAction("dismiss-act", a.id, "从列表移除这条记录")
            : a.remoteBaseUrl && a.promptId
              ? rowAction("cancel-activity", a.id, "取消：排队中的从队列摘除，正在渲染的中断")
              : null}
        </span>
      </div>
    );
  };

  const renderSwapRow = (t: SwapTask) => {
    const running = t.status === "running";
    return (
      <div key={t.id} className={ROW}>
        <span className={STATUS_SLOT}>
          {running ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
          ) : t.status === "done" ? (
            <Check className="h-3.5 w-3.5 text-emerald-500" />
          ) : (
            <X className="h-3.5 w-3.5 text-red-500" />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span
              className="truncate text-[12px] font-medium leading-5 text-gray-800"
              title={`${t.characterName} · ${t.faceLabel}`}
            >
              {t.characterName} · {t.faceLabel}
            </span>
            <span
              className={`ml-auto shrink-0 whitespace-nowrap font-mono text-[10px] tabular-nums ${
                running ? "text-primary" : "text-gray-400"
              }`}
            >
              {running ? fmtElapsed(t.startedAt, now) : t.finishedAt ? fmtElapsed(t.startedAt, t.finishedAt) : ""}
            </span>
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-gray-400">
            {running ? (
              <span className={`${PILL} bg-primary/10 text-primary`}>处理中</span>
            ) : t.status === "done" ? (
              <span className={`${PILL} bg-emerald-50 text-emerald-700`}>完成</span>
            ) : (
              <span className={`${PILL} bg-red-50 text-red-600`}>失败</span>
            )}
            <span className="shrink-0">换脸</span>
            <span className="truncate">
              {running
                ? "后台跑，约 2-4 分钟"
                : t.status === "done"
                  ? t.staleShots
                    ? `${t.staleShots} 个镜头已同步过期`
                    : ""
                  : t.error || "失败"}
            </span>
          </div>
          {running ? (
            <div className={TRACK}>
              <div className="h-full w-1/3 animate-pulse rounded-full bg-primary/70" />
            </div>
          ) : null}
        </div>

        <span className={ACTION_SLOT}>
          <button
            type="button"
            aria-label="从列表移除"
            onClick={() => dismissSwap(t.id)}
            className="flex h-5 w-5 items-center justify-center rounded-md text-gray-300 transition-colors hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            title={running ? "从列表移除（请求本身无法中断）" : "从列表移除"}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      </div>
    );
  };

  /** 落库作业（job_leases）：在跑的画真进度条；终态的画状态胶囊 + 耗时。
   *  🔴 2026-10-03「取消/删除」：在跑的行尾加「取消」（协作式：当前项跑完就停）；
   *  终态行加「删除」（真删 job_leases 历史行）；cancelled 是正式终态，不再误显示成「完成」。 */
  const renderLeaseRow = (l: LeaseRow, finished: boolean) => {
    const total = l.total ?? 0;
    const done = l.done ?? 0;
    // 🔴 2026-10-17（老板报「进度 100% 却一直处理中，为什么还不完成」）：
    //   `done` 的语义是「**已处理**块数」—— 分镜块的 `completedChunks` 在成功与失败两条
    //   路径上**都** +1（见 route.js 的 `__sspTouch`），所以「5 块全处理完」会被算成 100%，
    //   而实际可能只有 2 块成功、3 块因上游超时判死 → 用户看到「100% 但一动不动」，
    //   既无法判断跑没跑完，也看不到失败。这里把**失败块从进度里扣掉**（`okUnits` 才是真进度），
    //   并把失败数显式写在旁边；「失败」这个变量名已被下方整行状态占用，故用 `failedUnits`。
    const failedUnits = l.failed ?? 0;
    const okUnits = Math.max(0, done - failedUnits);
    const started = ms(l.startedAt) ?? now;
    const ended = ms(l.finishedAt);
    // 🔴 2026-10-18：进度改由服务端预估（`eta`）驱动 ——
    //   `okUnits/total` 在单镜任务上恒为 0，读不出任何进展；预估把「时间进度」也并进来。
    const eta = !finished ? l.eta : undefined;
    const elapsedMs = Math.max(0, now - started);
    const etaInfo = eta ? etaText(eta, elapsedMs) : null;
    const unitPct = total > 0 ? Math.min(100, Math.round((okUnits / total) * 100)) : 0;
    const pct = eta ? eta.pct : unitPct;
    const isCancelledRow = l.status === "cancelled";
    const failed = l.status === "failed" || l.status === "orphaned";
    return (
      <div key={l.id} className={ROW}>
        <span className={STATUS_SLOT}>
          {finished ? (
            failed ? (
              <X className="h-3.5 w-3.5 text-red-500" />
            ) : isCancelledRow ? (
              <X className="h-3.5 w-3.5 text-gray-400" />
            ) : (
              <Check className="h-3.5 w-3.5 text-emerald-500" />
            )
          ) : (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-[12px] font-medium leading-5 text-gray-800" title={leaseLabel(l)}>
              {leaseLabel(l)}
            </span>
            <span
              className={`ml-auto shrink-0 whitespace-nowrap font-mono text-[10px] tabular-nums ${
                finished ? "text-gray-400" : "text-primary"
              }`}
            >
              {finished ? (ended ? `耗时 ${fmtElapsed(started, ended)}` : "") : fmtElapsed(started, now)}
            </span>
          </div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-gray-400">
            {finished ? (
              failed ? (
                <span className={`${PILL} bg-red-50 text-red-600`}>
                  {l.status === "orphaned" ? "已回收" : "失败"}
                </span>
              ) : isCancelledRow ? (
                <span className={`${PILL} bg-gray-100 text-gray-500`}>已取消</span>
              ) : (
                <span className={`${PILL} bg-emerald-50 text-emerald-700`}>完成</span>
              )
            ) : failedUnits > 0 ? (
              <span className={`${PILL} bg-amber-50 text-amber-700`}>处理中</span>
            ) : (
              <span className={`${PILL} bg-primary/10 text-primary`}>处理中</span>
            )}
            {total > 0 ? (
              <span className="shrink-0 tabular-nums">
                成功 {okUnits}/{total}
              </span>
            ) : null}
            {etaInfo ? (
              <span
                className={`shrink-0 tabular-nums ${etaInfo.overrun ? "font-medium text-amber-600" : "text-primary/80"}`}
                title={etaInfo.tip}
              >
                {etaInfo.text}
              </span>
            ) : !finished && total > 0 && pct > 0 ? (
              <span className="shrink-0 tabular-nums">{pct}%</span>
            ) : null}
            {failedUnits > 0 ? (
              <span className="shrink-0 text-amber-600">失败 {failedUnits}</span>
            ) : null}
            {l.note ? (
              <span className={`truncate ${failed ? "text-red-500" : ""}`} title={l.note}>
                {l.note}
              </span>
            ) : null}
          </div>
          {!finished ? (
            <div className={TRACK} title={etaInfo?.tip}>
              {/* 🔴 2026-10-17：pct=0 时画「定宽脉冲」而不是 4% 的残段 ——
                  后者看起来像一根画坏的横线，用户读不出「正在跑、只是还没有完成块」。
                  2026-10-18：有预估后宽度改由 pct 驱动，超时换琥珀色。 */}
              {pct > 0 ? (
                <div
                  className={`h-full rounded-full transition-[width] duration-700 ${
                    etaInfo?.overrun ? "bg-amber-400" : "bg-primary"
                  }`}
                  style={{ width: `${pct}%` }}
                />
              ) : (
                <div className="h-full w-1/3 animate-pulse rounded-full bg-primary/70" />
              )}
            </div>
          ) : null}
        </div>

        <span className={ACTION_SLOT}>
          {finished
            ? rowAction("delete-lease", l.id, "删除这条历史记录")
            : rowAction("cancel-lease", l.id, "取消本批：当前项跑完后停止，未跑的不再继续")}
        </span>
      </div>
    );
  };

  const processingItems = [
    ...processingLive.map((a) => ({ key: `act:${a.id}`, node: renderLiveRow(a) })),
    ...runningSwap.map((t) => ({ key: `swap:${t.id}`, node: renderSwapRow(t) })),
    ...runningLeases.map((l) => ({ key: `lease:${l.id}`, node: renderLeaseRow(l, false) })),
  ];
  const queuedItems = queuedLive.map((a) => ({ key: `act:${a.id}`, node: renderLiveRow(a) }));
  const doneItems = doneList.map((e) => ({
    key: e.key,
    node:
      e.src === "act"
        ? renderLiveRow(e.a)
        : e.src === "swap"
          ? renderSwapRow(e.t)
          : renderLeaseRow(e.l, true),
  }));

  /**
   * 分页基数：
   *   · 合并视图（处理中栏）= 翻「已完成」那一段（量大的是它；处理中/排队中各只放一页）
   *   · 排队中 / 已完成栏 = 翻该栏自己的列表
   */
  const activeCount = tab === "queued" ? queuedCount : doneCount;
  const pages = Math.max(1, Math.ceil(activeCount / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), pages);
  const from = (safePage - 1) * PAGE_SIZE;
  const pageItems = (tab === "queued" ? queuedItems : doneItems).slice(from, from + PAGE_SIZE);

  /** 翻页条（两处共用）：>20 条才出现，caption 说清翻的是哪一段 */
  const pagerNode = (caption: string, total: number) =>
    pages <= 1 ? null : (
      <div className="flex items-center justify-between gap-2 border-t border-[var(--border-subtle)] px-3 py-1.5 text-[10px] text-gray-400">
        <button
          type="button"
          data-dock-page="prev"
          disabled={safePage <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          className="inline-flex h-5 items-center gap-0.5 rounded-md px-1 transition-colors enabled:hover:bg-gray-100 enabled:hover:text-gray-600 disabled:opacity-40"
        >
          <ChevronLeft className="h-3 w-3" />
          上一页
        </button>
        <span className="tabular-nums">
          {caption}
          {safePage}/{pages} · 共 {total} 条
        </span>
        <button
          type="button"
          data-dock-page="next"
          disabled={safePage >= pages}
          onClick={() => setPage((p) => Math.min(pages, p + 1))}
          className="inline-flex h-5 items-center gap-0.5 rounded-md px-1 transition-colors enabled:hover:bg-gray-100 enabled:hover:text-gray-600 disabled:opacity-40"
        >
          下一页
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>
    );

  const switchTab = (next: DockTab) => {
    setTab(next);
    setPage(1); // 换栏回第一页：留着上一栏的页码只会出现「第 3 页 / 空列表」
  };

  const TABS: { key: DockTab; label: string; count: number; Icon: typeof Clock; tone: string }[] = [
    { key: "processing", label: "处理中", count: processingCount, Icon: Loader2, tone: "text-primary" },
    { key: "queued", label: "排队中", count: queuedCount, Icon: Clock, tone: "text-amber-600" },
    { key: "done", label: "已完成", count: doneCount, Icon: Check, tone: "text-emerald-600" },
  ];

  /* 🔴 2026-10-17：空的段落直接不渲染 —— 旧版每段都留一行「没有排队中的任务」，
     三段里有两段是空的时，一屏全是废话。三段全空才显示整块空态。 */
  const showProcessing = processingItems.length > 0;
  const showQueued = queuedItems.length > 0;
  const showDone = doneItems.length > 0;
  const mergedEmpty = !showProcessing && !showQueued && !showDone;

  return (
    <div
      style={{ bottom: bottomOffset }}
      className="fixed right-4 z-50 flex w-[352px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-[var(--border-subtle)] bg-white shadow-2xl shadow-black/10"
    >
      {/* ① 头部：标题 + 实时摘要 + 窗口控制（回到顶部 / 收起 / 关闭）。
             整行可点折叠 —— 但右侧三个按钮各自 stopPropagation，避免「想回顶结果收起了面板」。 */}
      <div className="flex h-11 shrink-0 items-center gap-2 px-3">
        <button
          type="button"
          onClick={() => setExpanded(false)}
          aria-expanded
          data-dock-action="toggle-expand"
          title="收起面板"
          className="flex min-w-0 flex-1 items-center gap-2 text-left focus:outline-none"
        >
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px] bg-primary/10">
            <Layers className="h-3.5 w-3.5 text-primary" />
          </span>
          <span className="shrink-0 text-[13px] font-semibold text-gray-800">后台任务</span>
          {processingCount + queuedCount > 0 ? (
            <span className="flex min-w-0 items-center gap-1 text-[10px] text-gray-400">
              <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-primary" />
              <span className="truncate">
                {processingCount} 运行
                {queuedCount > 0 ? ` · ${queuedCount} 排队` : ""}
              </span>
            </span>
          ) : (
            <span className="truncate text-[10px] text-gray-400">空闲</span>
          )}
        </button>
        <div className="flex shrink-0 items-center gap-0.5">
          {/* 🔴 回到顶部（2026-10-03 用户要求，且「不能折叠」）：折叠态也渲染。
              2026-10-17：从整块实心主色胶囊收成 24px 图标钮 —— 上一轮「太不明显」的
              反馈靠**主色底**满足，不靠体量；它不该比「当前标签页」更重。
              2026-10-18：样式与折叠胶囊里的那枚统一走 `BACK_TO_TOP_BTN`
              （两处不同款 = 用户以为是两个功能）。 */}
          <button
            type="button"
            aria-label="回到顶部"
            data-dock-action="back-to-top"
            onClick={handleBackToTop}
            title="回到页面最上方"
            className={BACK_TO_TOP_BTN}
          >
            <ArrowUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label="收起面板"
            onClick={() => setExpanded(false)}
            title="收起为小胶囊"
            className={HEAD_ICON_BTN}
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label="关闭浮窗（下次有任务时自动回来）"
            onClick={() => setHidden(true)}
            title="关闭浮窗（下次有任务时自动回来）"
            className={HEAD_ICON_BTN}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* ② 标签页：分段控件（浅灰轨道 + 选中项白底浮起）。
             计数只在这里出现一次 —— 头部摘要只讲「运行/排队」，段落标题不带数字。 */}
      <div
        role="tablist"
        aria-label="后台任务分类"
        className="mx-3 mb-1 flex shrink-0 items-center gap-0.5 rounded-[10px] bg-[var(--surface)] p-0.5"
      >
        {TABS.map(({ key, label, count, Icon, tone }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              data-dock-tab={key}
              onClick={() => switchTab(key)}
              className={`flex h-7 flex-1 items-center justify-center gap-1 rounded-lg text-[11px] font-medium transition-all ${
                active
                  ? "bg-white text-gray-800 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              title={`${label} · ${count} 条`}
            >
              <Icon
                className={`h-3 w-3 shrink-0 ${
                  key === "processing" && count > 0 ? "animate-spin text-primary" : active ? "text-gray-500" : "text-gray-400"
                }`}
              />
              <span className="shrink-0">{label}</span>
              <span className={`shrink-0 text-[10px] font-semibold tabular-nums ${active ? tone : "text-gray-400"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ③ 列表：唯一可滚动区。高度随视口（58vh / 440px 取小）—— 旧版固定 320px，
             在 1080p 上只够看 4 行，而右边整块屏幕是空的。 */}
      <div className="max-h-[min(58vh,440px)] min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {tab === "processing" ? (
          /* ── 合并视图（2026-10-03 用户要求）──────────────────────────────
             「处理中的列表，要包含：处理中（置顶），排队中（第二段），已完成（第三段）」
             三段顺序固定、各有吸顶分隔符 —— 打开浮窗从上往下扫一遍就知道整体状态，
             不用来回切标签。后两段是**缩略段**（各最多一页 20 条）：看全部和翻页去各自标签页，
             这里的职责是「一眼看全」，不是「翻完所有历史」。 */
          <>
            {mergedEmpty ? <EmptyState text="暂无任务记录" /> : null}

            {showProcessing ? (
              <>
                <SectionLabel tone="bg-primary">处理中</SectionLabel>
                {processingItems.slice(0, PAGE_SIZE).map((it) => it.node)}
                {processingItems.length > PAGE_SIZE ? (
                  <MoreLine text={`还有 ${processingItems.length - PAGE_SIZE} 条处理中未显示`} />
                ) : null}
              </>
            ) : null}

            {showQueued ? (
              <>
                <SectionLabel tone="bg-amber-400">排队中</SectionLabel>
                {queuedItems.slice(0, PAGE_SIZE).map((it) => it.node)}
                {queuedItems.length > PAGE_SIZE ? (
                  <MoreLine text={`还有 ${queuedItems.length - PAGE_SIZE} 条排队中 · 去「排队中」栏翻页`} />
                ) : null}
              </>
            ) : null}

            {showDone ? (
              <>
                <SectionLabel tone="bg-emerald-400">已完成</SectionLabel>
                {pageItems.map((it) => it.node)}
              </>
            ) : null}
          </>
        ) : (
          <>
            {pageItems.length === 0 ? (
              <EmptyState text={tab === "queued" ? "没有排队中的任务" : "还没有已完成的记录"} />
            ) : (
              pageItems.map((it) => it.node)
            )}
          </>
        )}
      </div>

      {/* 🔴 2026-10-17：翻页条移到滚动区**外面**。
          旧版它排在列表末尾 —— 「已完成」有 566 条时，用户得先滚 20 行才看得见翻页按钮，
          等于把「还有下一页」藏起来了。现在固定在页脚上方，任何滚动位置都够得着。 */}
      {pagerNode(tab === "processing" ? "已完成 " : "", activeCount)}

      {/* ④ 页脚：动作条（左=顺序预览入口，右=停止 / 清除）。
             2026-10-17：动作从头部搬到这里 —— 头部只留窗口控制，避免三个按钮抢标题位置；
             原来那行「2.5 秒刷新 · 每页 20 条 · …」整行删除（说明进 title，不占视觉）。 */}
      <div className="flex shrink-0 items-center gap-2 border-t border-[var(--border-subtle)] px-2.5 py-2">
        {/* 🔴 2026-10-14：顺序预览入口在展开态也要在（否则任务面板一展开入口就消失）。 */}
        <ShotPreviewEntry />
        <span className="ml-auto" />
        {processingCount + queuedCount > 0 && (
          <button
            type="button"
            aria-label="停止全部任务"
            data-dock-action="stop-all"
            onClick={() => void stopAllTasks()}
            disabled={stoppingAll}
            title="停止全部批量作业、渲染任务与看门狗（批量作业：当前项跑完后停止；看门狗：回收暂停，入队新任务时自动恢复）"
            className="inline-flex h-6 items-center gap-1 rounded-md border border-red-200 bg-red-50/60 px-2 text-[10px] font-semibold text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
          >
            {stoppingAll ? <Loader2 className="h-3 w-3 animate-spin" /> : <Square className="h-2.5 w-2.5 fill-current" />}
            全部停止
          </button>
        )}
        {finishedCount > 0 && (
          <button
            type="button"
            onClick={clearSwapFinished}
            title="清除本会话的已完成记录（换脸）；落库作业的历史在「已完成」栏里，不受影响"
            className="inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-[10px] text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <Trash2 className="h-3 w-3" />
            清除
          </button>
        )}
      </div>
    </div>
  );
}
